import React, { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { StyledLogoContainer } from '../../AllJobs/style'
import { Briefcase, Clock, EmptyWallet, Location } from 'iconsax-react'
import { fToNow } from '../../../utils/helpers/formatTime'
import { formatJobType, formatScore } from '../../../utils/helpers/helpers'
import JobSaveButton from '../../Common/JobSaveButton/JobSaveButton'
import { StyledMatchCard, StyledSaveIcon, StyledTag } from './style'
import { IFindJobsJobItem } from '../../../types/findJobsType'
import FakeSaveButton from './FakeSaveButton'

type Props = {
  job: IFindJobsJobItem
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const JobCard: React.FC<Props> = ({ job, setIsModalOpen }) => {
  const router = useRouter()
  const theme = useTheme()
  const { id: resumeId } = router.query

  const [isSaved, setIsSaved] = useState<boolean>(job.is_saved)
  const [link, setLink] = useState(
    job.logo || '/images/resource/logo-placeholder.png'
  )

  return (
    <StyledMatchCard>
      <Stack direction={'row'} spacing={2.5} alignItems={'center'}>
        <StyledLogoContainer>
          {/*  eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={link}
            alt={'client logo'}
            onError={() => {
              setLink('/images/resource/logo-placeholder.png')
            }}
          />
        </StyledLogoContainer>
        <Stack spacing={0.5}>
          {resumeId ? (
            <Link href={`/r/${resumeId}/job-posts/${job.job_id}`}>
              <Typography variant="body_m_medium">{job.title}</Typography>
            </Link>
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
              }}
              onClick={() => {
                setIsModalOpen(true)
              }}
            >
              <Typography variant="body_m_medium">{job.title}</Typography>
            </Box>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }}
            spacing={{ xs: 1, sm: 2.5, md: 1, lg: 2.5 }}
            color={theme.palette.darkGray.main}
          >
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <Briefcase size="17" />
              <Typography variant="body_s_regular" color={'inherit'}>
                {job.platform && job.platform}
              </Typography>
            </Stack>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <Location size="17" />
              <Typography
                variant="body_s_regular"
                color={'inherit'}
                sx={{
                  maxWidth: {
                    xs: 'unset',
                    sm: '120px',
                    md: 'unset',
                    lg: '120px',
                    xl: 'unset',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {job.contact_country} / {job.contact_city}
              </Typography>
            </Stack>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <Clock size="17" />
              <Typography variant="body_s_regular" color={'inherit'}>
                {fToNow(job.date)}
              </Typography>
            </Stack>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <EmptyWallet size="17" />
              <Typography variant="body_s_regular" color={'inherit'}>
                {'-'}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction={'row'} spacing={1} style={{ marginTop: 10 }}>
            {job.matching_rate && (
              <Box>
                <StyledTag score>
                  <Typography variant="detail_regular">
                    {formatScore(job.matching_rate)}
                  </Typography>
                </StyledTag>
              </Box>
            )}
            {job.project_type && (
              <Box>
                <StyledTag>
                  <Typography variant="detail_regular">
                    {formatJobType(job.project_type)}
                  </Typography>
                </StyledTag>
              </Box>
            )}
            {job.workplace && (
              <Box>
                <StyledTag>
                  <Typography variant="detail_regular">
                    {job.workplace}
                  </Typography>
                </StyledTag>
              </Box>
            )}
            {job.career_level && (
              <Box>
                <StyledTag>
                  <Typography variant="detail_regular">
                    {job.career_level}
                  </Typography>
                </StyledTag>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
      <StyledSaveIcon
        onClick={() => {
          if (resumeId) {
            setIsSaved(prev => !prev)
          } else {
            setIsModalOpen(true)
          }
        }}
      >
        {resumeId ? (
          <JobSaveButton jobId={job.job_id} isSaved={isSaved} />
        ) : (
          <FakeSaveButton />
        )}
      </StyledSaveIcon>
    </StyledMatchCard>
  )
}

export default JobCard
